import { useContext, useMemo } from 'react'
import { UserPermissionsContext } from 'app/hooks/useUserPermission'
import { legacyRoleRank } from 'app/types/roles'
import { useUser } from 'app/utils'

/**
 * Hook that returns whether the current user is an org-level user.
 * Reads the role from the permissions context, with a numeric-role fallback
 * while permissions are still loading.
 */
export function useIsOrgUser(): boolean {
  const user = useUser()
  const { permissions, isOrgRole } = useContext(UserPermissionsContext)

  return useMemo(() => {
    // undefined = not loaded or error state = not org user
    if (!user) return false
    // Super users are always org-level (also handled by provider, but safe guard)
    if (user.type === `super`) return true
    // Permissions loaded: use context value
    if (permissions !== undefined) {
      return isOrgRole
    }
    // Fallback while permissions load: check numeric role
    return user.role !== undefined && user.role >= legacyRoleRank.orgUser
  }, [user, user?.type, user?.role, permissions, isOrgRole])
}
